import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react'
import MuiAvatar, { AvatarProps as MuiAvatarProps } from '@mui/material/Avatar'
import { PhotoSubjectType, isBrandMarkSubject } from '@common/interfaces/photo'
import cn from 'classnames'
import { avatarColors, avatarInitials } from '../../../helpers/avatarPalette'
import { addLogoDevToken } from '../../../helpers/helpers'
import styles from './ProfilePhoto.module.scss'
import { loadPhoto, subscribeToPhoto } from './photoCache'

export type ProfilePhotoSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge'

/**
 * The classes for a size, for the rare avatar this component doesn't render itself — MUI's
 * AvatarGroup builds the "+N" surplus disc on its own, and it has to match the faces it sits
 * beside. Both classes, not just the size: `.avatar` carries the weight and letter-spacing, and
 * MUI sets no font-weight of its own, so geometry alone leaves "+N" lighter than the initials
 * next to it.
 */
export const profilePhotoSizeClass = (size: ProfilePhotoSize): string =>
  cn(styles.avatar, styles[size])

/**
 * The props ProfilePhoto sets on the avatar itself. Everything else a caller passes — `ref`, the
 * hover handlers and aria attributes a MUI Tooltip clones onto its child — rides through to the
 * rendered element, so an avatar can anchor a tooltip or a popper without a wrapper span (a
 * wrapper is what breaks AvatarGroup's overlap, since the group's className would land on the
 * span instead of the avatar).
 */
type ForwardedAvatarProps = Omit<
  MuiAvatarProps,
  'src' | 'alt' | 'variant' | 'children' | 'className' | 'style' | 'imgProps'
>

export interface IProps extends ForwardedAvatarProps {
  /** Display name — drives the initials fallback, the color, and the alt text. */
  name: string | null | undefined
  /**
   * Which subject this is and its id. Given both, the photo is fetched (once per subject per
   * session) and kept in sync with uploads. Omit them when the photo is already in hand.
   */
  subjectType?: PhotoSubjectType
  id?: string | null
  /**
   * An already-resolved photo. Wins over fetching — used where the payload already carries the
   * image (the person detail page) so the first paint has no round trip.
   */
  photo?: string | null
  /**
   * From a list payload: `false` means "this subject has no photo, don't ask". Skips the request
   * for the common photo-less row. Leave undefined when unknown and it will be fetched.
   */
  hasPhoto?: boolean
  /**
   * Non-person fallback image — a Logo.dev company mark. Used when there's no uploaded photo,
   * ahead of initials. Passed through addLogoDevToken, so hand it the raw stored value.
   */
  iconUrl?: string | null
  /**
   * Force the brand-mark treatment (rounded square, `contain`) on or off. Defaults to
   * isBrandMarkSubject(subjectType) — faces are circles, logos are rounded squares — so a caller
   * normally passes nothing and cannot mismatch the shape to the subject.
   */
  isLogo?: boolean
  size?: ProfilePhotoSize
  /**
   * Show a presence dot: `true` online, `false` offline, omitted for no
   * indicator at all.
   *
   * Tri-state rather than a boolean so the overwhelming majority of call sites —
   * every avatar that has nothing to do with presence — keep rendering exactly
   * the markup they did before. See the wrapper at the bottom of this file.
   */
  isOnline?: boolean
  className?: string
  'data-cy'?: string
}

/**
 * A subject's photo — a person, user, client, entity or company — falling back to their initials.
 *
 * The avatar renderer. It absorbed three implementations that each got a different subset right:
 * Common/Avatar (no color, so every initials avatar was the same grey disc), Common/EditableAvatar
 * (colored via the broken stringToColor, threw on a trailing space in the name) and
 * Messages/MemberAvatar (fetch-by-id + cache, but one initial only and users only). The first and
 * third are now thin adapters over this; the second is gone.
 *
 * Every person avatar in the app now renders through here, which is what makes a photo appear
 * consistently: a user's can live in S3, in `users.avatar` or on their Google account, and only
 * the photo endpoint applies that precedence. A surface that reads the columns itself shows a
 * face for the handful of legacy accounts and initials for everyone who has since uploaded one.
 */
const ProfilePhoto = ({
  name,
  subjectType,
  id,
  photo,
  hasPhoto,
  iconUrl,
  isLogo,
  size = 'medium',
  isOnline,
  className,
  'data-cy': dataCy,
  ...rest
}: IProps) => {
  // `undefined` = nothing resolved for this subject yet, which is distinct from `null` = resolved
  // to "no photo". The distinction is what lets a value that arrives later (a fetch, or an upload
  // priming the cache) override the `photo` prop — without it, any caller passing `photo` would
  // pin the old image forever, since a primed cache update could never win.
  const [fetched, setFetched] = useState<string | null | undefined>(undefined)
  const [isBroken, setIsBroken] = useState(false)

  // Drop the previous subject's resolved photo the moment the identity changes, in the same
  // render rather than in an effect. The `isCurrent` guard below only stops a late response
  // landing on the wrong subject; it does nothing about a value that already resolved, which
  // stays in `fetched` and keeps painting until the new lookup returns. A row that unmounts
  // never notices, but an instance that outlives the change does — the global header keeps one
  // avatar across CRM route changes, and without this it shows the previous record's face
  // beside the new page title.
  const identity = `${subjectType ?? ''}:${id ?? ''}`
  const [renderedIdentity, setRenderedIdentity] = useState(identity)
  if (identity !== renderedIdentity) {
    setRenderedIdentity(identity)
    setFetched(undefined)
    setIsBroken(false)
  }

  // Fetch only when the photo isn't already in hand and the payload hasn't told us there is none.
  const shouldFetch = !!subjectType && !!id && photo === undefined && hasPhoto !== false

  const refresh = useCallback(() => {
    if (!shouldFetch || !subjectType || !id) return undefined

    let isCurrent = true
    void loadPhoto(subjectType, id).then((resolved) => {
      // A recycled row (a virtualized list scrolling onto a different subject) must not adopt
      // the photo its previous occupant was still waiting on.
      if (isCurrent) setFetched(resolved)
    })

    return () => {
      isCurrent = false
    }
  }, [shouldFetch, subjectType, id])

  useEffect(() => refresh(), [refresh])

  // Repaint when this subject's photo is replaced elsewhere — the editor primes the cache, and
  // every mounted avatar for that subject picks the new image up without the page re-fetching.
  useEffect(() => {
    if (!subjectType || !id) return undefined
    return subscribeToPhoto(subjectType, id, () => {
      void loadPhoto(subjectType, id).then(setFetched)
    })
  }, [subjectType, id])

  const src = useMemo(() => {
    const resolved = fetched !== undefined ? fetched : photo
    return resolved || addLogoDevToken(iconUrl) || null
  }, [photo, fetched, iconUrl])

  // Reset the error flag when the source changes, so a newly uploaded photo can render after a
  // previously broken one failed to load.
  useEffect(() => {
    setIsBroken(false)
  }, [src])

  const displayName = name ?? ''
  const initials = useMemo(() => avatarInitials(displayName), [displayName])
  const colors = useMemo(() => avatarColors(displayName), [displayName])

  const isBrandMark = isLogo ?? isBrandMarkSubject(subjectType)
  // `variant` rather than CSS alone: MUI sets `border-radius: 50%` in the Avatar's own emotion
  // class, which wins over a single-class CSS-module selector, so a stylesheet-only rule left
  // brand marks circular. The stylesheet then only refines the radius to our token.
  const variant = isBrandMark ? 'rounded' : 'circular'
  const rootClassName = cn(styles.avatar, styles[size], { [styles.logo]: isBrandMark }, className)

  const avatar =
    src && !isBroken ? (
      <MuiAvatar
        {...rest}
        className={rootClassName}
        variant={variant}
        data-cy={dataCy}
        src={src}
        alt={displayName || undefined}
        // MUI renders `children` when `src` fails, but keeps the img element's own error silent —
        // tracking it here is what lets a dead URL fall back to colored initials rather than an
        // empty grey disc.
        imgProps={{ onError: () => setIsBroken(true) }}
      >
        {initials}
      </MuiAvatar>
    ) : (
      <MuiAvatar
        {...rest}
        className={cn(rootClassName, styles.initials)}
        variant={variant}
        data-cy={dataCy}
        style={
          { '--avatar-bg': colors.backgroundColor, '--avatar-text': colors.color } as CSSProperties
        }
        alt={displayName || undefined}
      >
        {initials}
      </MuiAvatar>
    )

  // The overwhelming majority of avatars in the app show no presence, and they
  // return here with exactly the markup this component emitted before presence
  // existed — no wrapper element, so no call site's layout moves. Only an avatar
  // that actually asked for a dot gets the positioned span.
  //
  // The status emoji was a second badge here and is not any more: it reads as a
  // smudge at the sizes these render at, so it moved beside the name — see
  // `Common/AimStatus`.
  if (isOnline === undefined) return avatar

  return (
    <span className={styles.decorated}>
      {avatar}
      <span
        className={cn(styles.presenceDot, { [styles.presenceOnline]: isOnline })}
        data-cy="presence-dot"
      />
    </span>
  )
}

export default ProfilePhoto
